import type {
  InteractionBinding,
  InteractionEvent,
  JsonObject,
  JsonPrimitive,
} from '../contracts/index.ts';
import type { RuntimeHost } from '../runtime/index.ts';
import { z } from 'zod';
import { jsonObjectSchema, jsonValueSchema } from '../contracts/index.ts';

export const interactionActionDescriptorSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+$/),
  title: z.string().min(1),
  description: z.string().default(''),
  requiresRuntime: z.boolean().default(false),
  paramsSchema: jsonObjectSchema.default({}),
}).strict();

export type InteractionActionDescriptor = z.infer<
  typeof interactionActionDescriptorSchema
>;

export interface InteractionActionContext {
  event: InteractionEvent;
  binding: InteractionBinding;
  runtimeHost?: RuntimeHost;
  presentation?: InteractionPresentationController;
}

export interface BlockDataFilter {
  field: string;
  values: JsonPrimitive[];
  mode: 'include' | 'exclude';
}

export interface BlockPresentationState {
  visible: boolean;
  highlighted: boolean;
  focused: boolean;
  filter: BlockDataFilter | null;
}

export interface InteractionPresentationController {
  patchBlocks: (
    blockIds: string[],
    patch: Partial<BlockPresentationState>,
  ) => void;
  clearSelection: () => void;
}

export interface InteractionActionDefinition {
  descriptor: InteractionActionDescriptor;
  paramsSchema: z.ZodType<JsonObject>;
  execute: (context: InteractionActionContext) => void | Promise<void>;
}

export class InteractionActionRegistry {
  private readonly definitions = new Map<
    string,
    InteractionActionDefinition
  >();

  register(rawDefinition: InteractionActionDefinition): this {
    const descriptor = interactionActionDescriptorSchema.parse(
      rawDefinition.descriptor,
    );
    if (this.definitions.has(descriptor.key))
      throw new Error(`交互动作已经注册：${descriptor.key}`);
    this.definitions.set(descriptor.key, {
      ...rawDefinition,
      descriptor,
    });
    return this;
  }

  has(key: string): boolean {
    return this.definitions.has(key);
  }

  resolve(key: string): InteractionActionDefinition {
    const definition = this.definitions.get(key);
    if (!definition)
      throw new Error(`交互动作尚未注册：${key}`);
    return definition;
  }

  listDescriptors(): InteractionActionDescriptor[] {
    return [...this.definitions.values()].map(definition =>
      interactionActionDescriptorSchema.parse(
        structuredClone(definition.descriptor),
      ),
    );
  }
}

function requireRuntime(
  context: InteractionActionContext,
): { host: RuntimeHost; runtimeId: string } {
  if (!context.runtimeHost)
    throw new Error('当前交互动作需要 RuntimeHost，但尚未配置');
  if (!context.binding.runtimeId)
    throw new Error(`交互动作缺少 runtimeId：${context.binding.action}`);
  return {
    host: context.runtimeHost,
    runtimeId: context.binding.runtimeId,
  };
}

function eventPayload(event: InteractionEvent): JsonObject {
  return typeof event.payload === 'object'
    && event.payload !== null
    && !Array.isArray(event.payload)
    ? event.payload
    : { value: event.payload };
}

function requirePresentation(
  context: InteractionActionContext,
): InteractionPresentationController {
  if (!context.presentation)
    throw new Error('当前交互动作需要展示状态控制器，但尚未配置');
  return context.presentation;
}

function requireTargets(context: InteractionActionContext): string[] {
  if (context.binding.targetBlockIds.length === 0)
    throw new Error(`交互动作缺少目标 Block：${context.binding.action}`);
  return context.binding.targetBlockIds;
}

export function createBuiltinInteractionActionRegistry():
InteractionActionRegistry {
  return new InteractionActionRegistry()
    .register({
      descriptor: {
        key: 'runtime.reset',
        title: '重置运行会话',
        description: '将指定 Runtime 恢复到初始状态。',
        requiresRuntime: true,
        paramsSchema: {},
      },
      paramsSchema: z.object({}).strict(),
      execute: async (context) => {
        const { host, runtimeId } = requireRuntime(context);
        await host.dispatch(runtimeId, { type: 'reset' });
      },
    })
    .register(createPresentationPatchAction(
      'presentation.show',
      '显示内容块',
      '显示目标 Block。',
      { visible: true },
    ))
    .register(createPresentationPatchAction(
      'presentation.hide',
      '隐藏内容块',
      '隐藏目标 Block，但不删除项目配置。',
      { visible: false },
    ))
    .register(createPresentationPatchAction(
      'presentation.highlight',
      '高亮内容块',
      '高亮目标 Block 以引导观察。',
      { highlighted: true },
    ))
    .register(createPresentationPatchAction(
      'presentation.unhighlight',
      '取消高亮',
      '取消目标 Block 的展示高亮。',
      { highlighted: false },
    ))
    .register(createPresentationPatchAction(
      'presentation.focus',
      '聚焦内容块',
      '聚焦并滚动到目标 Block；场景可结合 selection camera 跟随实体。',
      { focused: true, visible: true },
    ))
    .register({
      descriptor: {
        key: 'presentation.filter',
        title: '筛选内容块数据',
        description: '按字段和值筛选目标 Block 消费的数据。',
        requiresRuntime: false,
        paramsSchema: {
          type: 'object',
          required: ['field', 'values'],
          properties: {
            field: { type: 'string' },
            values: { type: 'array' },
            mode: {
              type: 'string',
              enum: ['include', 'exclude', 'clear'],
            },
          },
          additionalProperties: false,
        },
      },
      paramsSchema: z.object({
        field: z.string().min(1),
        values: z.array(jsonValueSchema).max(10000).default([]),
        mode: z.enum(['include', 'exclude', 'clear']).default('include'),
      }).strict().transform(params => params as JsonObject),
      execute: (context) => {
        const controller = requirePresentation(context);
        const targets = requireTargets(context);
        const mode = context.binding.params.mode;
        controller.patchBlocks(targets, {
          filter: mode === 'clear'
            ? null
            : {
                field: String(context.binding.params.field),
                values: Array.isArray(context.binding.params.values)
                  ? context.binding.params.values as JsonPrimitive[]
                  : [],
                mode: mode === 'exclude' ? 'exclude' : 'include',
              },
        });
      },
    })
    .register({
      descriptor: {
        key: 'selection.clear',
        title: '清空共享选择',
        description: '清空场景和图表共享的实体选择。',
        requiresRuntime: false,
        paramsSchema: {},
      },
      paramsSchema: z.object({}).strict(),
      execute: (context) => {
        requirePresentation(context).clearSelection();
      },
    })
    .register({
      descriptor: {
        key: 'runtime.step',
        title: '运行单步',
        description: '让指定 Runtime 向前执行一个模型步。',
        requiresRuntime: true,
        paramsSchema: {},
      },
      paramsSchema: z.object({}).strict(),
      execute: async (context) => {
        const { host, runtimeId } = requireRuntime(context);
        await host.dispatch(runtimeId, { type: 'step', steps: 1 });
      },
    })
    .register({
      descriptor: {
        key: 'model.interaction',
        title: '发送模型交互',
        description: '向声明式模型发送受控 Interaction 输入。',
        requiresRuntime: true,
        paramsSchema: {
          type: 'object',
          properties: {
            eventType: { type: 'string' },
          },
          additionalProperties: true,
        },
      },
      paramsSchema: z.object({
        eventType: z.string().min(1).optional(),
      }).catchall(jsonValueSchema),
      execute: async (context) => {
        const { host, runtimeId } = requireRuntime(context);
        const configuredType = context.binding.params.eventType;
        const {
          eventType: _eventType,
          ...configuredPayload
        } = context.binding.params;
        await host.dispatch(runtimeId, {
          type: 'interaction.dispatch',
          eventType: typeof configuredType === 'string'
            ? configuredType
            : context.event.type,
          payload: {
            ...configuredPayload,
            ...eventPayload(context.event),
          },
        });
      },
    });
}

function createPresentationPatchAction(
  key: string,
  title: string,
  description: string,
  patch: Partial<BlockPresentationState>,
): InteractionActionDefinition {
  return {
    descriptor: {
      key,
      title,
      description,
      requiresRuntime: false,
      paramsSchema: {},
    },
    paramsSchema: z.object({}).strict(),
    execute: (context) => {
      requirePresentation(context).patchBlocks(
        requireTargets(context),
        patch,
      );
    },
  };
}
